//eslint-disable-next-line
//@ts-nocheck

import {
  CustomIndicator,
  PineJS,
  RawStudyMetaInfoId,
  StudyInputType,
  StudyPlotType,
  StudyTargetPriceScale,
} from "@/lib/datafeed/tvTypes.ts";

import { LineStudyPlotStyle } from "@/lib/datafeed/utils.ts";

import {
  ParseSymbol,
  AccessCheck,
} from "@/lib/indicators/indicators-common-functions";

import { IIndicatorOptions } from "@/domain/interfaces/general.interface";

export const activityDetector = (
  PineJS: PineJS,
  options?: IIndicatorOptions,
): CustomIndicator => {
  return {
    name: "Activity detector",
    metainfo: {
      _metainfoVersion: 51,
      id: "activity-detector@tv-basicstudies-1" as RawStudyMetaInfoId,
      description: "[SFM] Activity detector",
      shortDescription: "[SF] Activity detector",
      is_hidden_study: false,
      is_price_study: false,
      isCustomIndicator: true,
      linkedToSeries: false,
      format: {
        type: "price",
        precision: 3,
      },
      plots: [
        {
          id: "zeroline",
          type: StudyPlotType.Line,
        },
        {
          id: "activityDetectorPlot",
          type: StudyPlotType.Line,
        },
        {
          id: "altmeterColorer",
          type: StudyPlotType.Colorer,
          target: "activityDetectorPlot",
          palette: "activityDetectorPalette",
        },
        {
          id: "backgroundPlot",
          type: StudyPlotType.BgColorer,
          palette: "backgroundPalette",
        },
      ],
      palettes: {
        activityDetectorPalette: {
          colors: {
            0: { name: "No activity" },
            1: { name: "Activity" },
          },
          valToIndex: {
            0: 0,
            1: 1,
          },
        },
        backgroundPalette: {
          colors: [
            { name: "Activity" },
            { name: "Activity" },
            { name: "Activity" },
            { name: "Activity" },
            { name: "Activity" },
            { name: "Activity" },
            { name: "Activity" },
            { name: "Activity" },
            { name: "Activity" },
            { name: "Activity" },
          ],
          valToIndex: {
            0: 0,
            1: 1,
            2: 2,
            3: 3,
            4: 4,
            5: 5,
            6: 6,
            7: 7,
            8: 8,
            9: 9,
          },
        },
      },
      defaults: {
        palettes: {
          activityDetectorPalette: {
            colors: {
              0: { color: "rgba(149, 152, 161, 0.5)", width: 1, style: 0 },
              1: { color: "rgba(13, 157, 219, 0.5)", width: 1, style: 0 },
            },
          },
          backgroundPalette: {
            colors: [
              { color: "rgba(13, 157, 219, 0.05)" },
              { color: "rgba(13, 157, 219, 0.10)" },
              { color: "rgba(13, 157, 219, 0.15)" },
              { color: "rgba(13, 157, 219, 0.20)" },
              { color: "rgba(13, 157, 219, 0.25)" },
              { color: "rgba(13, 157, 219, 0.30)" },
              { color: "rgba(13, 157, 219, 0.35)" },
              { color: "rgba(13, 157, 219, 0.40)" },
              { color: "rgba(13, 157, 219, 0.45)" },
              { color: "rgba(13, 157, 219, 0.50)" },
            ],
          },
        },
        styles: {
          zeroline: {
            linestyle: 2,
            visible: true,
            linewidth: 1,
            display: 3,
            color: "rgb(149, 152, 161)",
          },
          activityDetectorPlot: {
            linestyle: 0,
            visible: true,
            plottype: LineStudyPlotStyle.Area,
            linewidth: 1,
            trackPrice: false,
            color: "rgb(13, 157, 219)",
          },
        },
        precision: 0,
        inputs: {
          symbolInput: "",
        },
      },
      styles: {
        zeroline: {
          title: "Zeroline",
          histogramBase: 0,
        },
        activityDetectorPlot: {
          title: "Activity detector",
          histogramBase: 0,
        },
        backgroundPlot: {
          title: "Background",
          histogramBase: 0,
        },
      },
      inputs: [
        {
          id: "symbolInput",
          name: "Symbol",
          defval: "",
          type: StudyInputType.Symbol,
        },
      ],
    },

    constructor: function () {
      this.init = function (context: any, inputCallback: any): void {
        this._context = context;
        this._input = inputCallback;

        const period: number = PineJS.Std.period(this._context);

        const symbol: string = this._input(0) || this._context.symbol.info.name;
        const baseSymbol: string = ParseSymbol(symbol);
        const activityDetectorSymbol: string = `${baseSymbol}#SF_ACTIVITY_DETECTOR`;
        this._context.new_sym(activityDetectorSymbol, period);
      };

      this.main = function (context: any, inputCallback: any): number[] {
        this._context = context;
        this._input = inputCallback;

        // Market data parsing
        this._context.select_sym(1);
        const [activityDetectorTime, activityDetectorArray]: any[] = [
          this._context.new_var(this._context.symbol.time),
          this._context.new_var(PineJS.Std.close(this._context)),
        ];

        this._context.select_sym(0);
        const mainSymbolTime: any = this._context.new_var(
          this._context.symbol.time,
        );
        const activityDetector: number = activityDetectorArray.adopt(
          activityDetectorTime,
          mainSymbolTime,
          0,
        );

        // Calculations
        const activityDetectorColor: number = Number(activityDetector >= 0);

        const backgroundColor: number =
          activityDetector >= 0
            ? Math.min(Math.floor(activityDetector * 10), 9)
            : NaN;

        const shouldShowData: boolean = AccessCheck(
          PineJS,
          this._context,
          options,
        );

        if (shouldShowData) {
          return [0, activityDetector, activityDetectorColor, backgroundColor];
        } else {
          return [NaN, NaN, NaN, NaN];
        }
      };
    },
  };
};
